import React from "react";
import { Button, Tag } from "antd";
import type { TablePaginationConfig, ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { User } from "types/superAdmin/userTypes";
import Datatable from "components/Common/dataTable";
import UserRoleBadge from "components/Common/Badge/UserRoleBadge";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import StatusBadge from "components/Common/StatusBadge";
import UserStatusBadge from "components/Common/Badge/UserStatusBadge";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";

interface UsersTableProps {
    dataSource: User[];
    loading: boolean;
    pagination?: boolean;
    dataPaginate?: {
        current?: number;
        pageSize?: number;
        total?: number;
    };
    onChange?: (pg: TablePaginationConfig) => void;
    onUpdateStatus: (record: User, status: "active" | "deleted") => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
    dataSource,
    loading,
    pagination = true,
    dataPaginate,
    onChange,
    onUpdateStatus,
}) => {
    const { t } = useTranslation();

    const columns: ColumnsType<User> = [
        {
            title: t("user"),
            key: "user",
            render: (_, record) =>
                <UserProfileHeader
                    firstName={record.firstName}
                    lastName={record.lastName}
                    photo={record?.photo}
                />,
        },
        {
            title: t("email"),
            dataIndex: "email",
            key: "email",
        },
        {
            title: t("role"),
            dataIndex: "role",
            key: "role",
            render: (role: string) => <UserRoleBadge role={role} />,
        },
        {
            title: t("status"),
            dataIndex: "status",
            key: "status",
            render: (status: string) => {
                return <UserStatusBadge status={status as "active" | "inactive" | "deleted"} />;
            },
        },
        {
            title: t("actions"),
            key: "actions",
            align: "right",
            render: (_, record) => {
                const actions: Action[] = [
                    {
                        label: t("enable"),
                        icon: "ti ti-check",
                        actionType: "link",
                        hidden: record.status !== "deleted",
                        onClick: () => onUpdateStatus(record, "active"),
                    },
                    {
                        label: t("delete"),
                        icon: "ti ti-trash",
                        actionType: "link",
                        hidden: record.status !== "active",
                        onClick: () => onUpdateStatus(record, "deleted"),
                    },

                ];
                return actions?.length > 0 ? <ActionsDropdown actions={actions} /> : <i className="ti ti-lock"></i>;
            },
        },
    ];

    return (
        <Datatable
            rowKey="id"
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={pagination}
            dataPaginate={dataPaginate}
            onChange={(pg: any) => {
                onChange && onChange(pg);
            }}
        />
    );
};

export default UsersTable;
